/**
 * Server functions for usage-over-time charts. Each call re-checks the session
 * and scopes the query itself (a route guard does NOT protect a server fn).
 *
 * Control-plane byte counters are cumulative, so every chart here is a
 * lag-delta series: consumed-per-day is the positive change between consecutive
 * snapshots of the same subject (counter regressions clamped to 0), bucketed by
 * UTC day. Deltas are computed over ALL of a subject's snapshots and only THEN
 * filtered to the window, so the first day inside the window still gets a
 * correct delta from the snapshot just before it.
 *
 * There are two snapshot series, and which one a chart reads matters:
 *  - `group_usage_snapshots` (per ORG, one row per UTC day) backs the org-level
 *    and product-wide charts. This is the SAME cumulative group counter billing
 *    derives `usedThisCycle` from, so those chart totals and the bill agree up
 *    to metering-tick timing (a chart day closes at the last tick before
 *    midnight UTC, not at midnight itself).
 *  - `usage_snapshots` (per CREDENTIAL) backs the per-credential detail views
 *    (`getCredentialUsage`, `getOrgCredentialUsage`). It is a second, finer
 *    instrument sampling different counters, so summing it can differ from the
 *    group series by small bounded amounts — per-credential regression clamping
 *    hides an interval the group counter still carried, and a credential
 *    deleted mid-tick stops contributing before the group counter does. That
 *    residual divergence is expected and confined to per-credential views; do
 *    NOT try to reconcile the two series.
 *
 * Not marked `server-only`: this exports `createServerFn`s the client imports as
 * RPC stubs. The compiler strips the `.handler()` bodies (and the server-only
 * imports they use — db, auth) from the client bundle.
 */
import { createServerFn } from '@tanstack/react-start'
import { and, eq, type SQL, sql } from 'drizzle-orm'
import { z } from 'zod'

import { db } from '@/db'
import { proxyCredentials } from '@/db/schema'
import { requirePlatformAdmin } from '@/lib/admin/require-admin'
import { PublicError } from '@/lib/errors'
import { requireOrgMember } from '@/lib/org/require-org.server'
import {
  fillDailySeries,
  normalizeRange,
  rangeDays,
  type UsagePoint,
  type UsageRange,
} from '@/lib/usage/usage'
import { parseInput, id as zId } from '@/lib/validation'

/**
 * Per-snapshot consumed-bytes delta from a cumulative counter: the positive
 * change since the previous snapshot, clamped at 0. ONE expression for both the
 * per-credential and the per-org group series — only the window partition
 * differs (declared per-series below).
 *
 * First row (no predecessor) → 0. This is load-bearing for consistency: the
 * usage page shows the org headline/chart (group series) and the per-credential
 * breakdown (credential series) side by side, and a customer reads them against
 * each other. If the two used DIFFERENT first-row rules — the group dropping
 * its first day while each credential counted its full first cumulative — the
 * bars would sum to MORE than the headline on every org (by the first day's
 * traffic). Same rule both sides → they reconcile. The rule is also right on its
 * own merits: a counter that predates this table (an org, or a credential
 * migrated in) would otherwise dump its entire history onto the first snapshot's
 * day as one spike. Genuinely-new counters instead get a 0 genesis anchor dated
 * the day before creation (credentials/server.ts) so their first real day shows
 * real usage; only a counter with no such anchor (e.g. one migrated in) still
 * reads 0 on its first day.
 */
const consumedDeltaSql = sql`greatest(0, CASE
  WHEN lag(s.cumulative_bytes) OVER w IS NULL THEN 0
  WHEN s.cumulative_bytes >= lag(s.cumulative_bytes) OVER w
    THEN s.cumulative_bytes - lag(s.cumulative_bytes) OVER w
  -- Counter regression (backend reset/loss): show 0 for that interval, matching
  -- the monotonic clamp on the billing side so the chart never shows usage from
  -- an upstream counter rollback.
  ELSE 0
END)`

/** Alias kept for the group-series call sites' readability. */
const groupConsumedDeltaSql = consumedDeltaSql

/**
 * A snapshot series' delta expression paired with the window it requires — the
 * two must move together, so they are declared as one unit rather than passed
 * separately.
 */
interface SnapshotSeries {
  delta: SQL
  window: SQL
}

/** Per-credential series (`usage_snapshots`): credential-detail charts. */
const credentialSeries: SnapshotSeries = {
  delta: consumedDeltaSql,
  window: sql`PARTITION BY s.credential_id ORDER BY s.captured_at`,
}

/** Per-org group series (`group_usage_snapshots`): org + product charts, same counter as the bill. */
const groupSeries: SnapshotSeries = {
  delta: groupConsumedDeltaSql,
  window: sql`PARTITION BY s.organization_id ORDER BY s.captured_at`,
}

/**
 * Daily consumed-bytes series for a set of snapshot rows, scoped by `fromWhere`
 * (the `FROM`-clause tail: tables + optional join + scoping `WHERE`) and read
 * with `series`' delta/window pair — `fromWhere` MUST name the table that
 * series belongs to. Returns a gap-filled series for the range.
 */
async function dailyUsage(
  fromWhere: SQL,
  range: UsageRange,
  series: SnapshotSeries,
): Promise<UsagePoint[]> {
  const days = rangeDays(range)
  const sinceFilter = days
    ? sql`WHERE d.captured_at >= now() - (${sql.raw(String(days))} || ' days')::interval`
    : sql``

  const rows = await db.execute<{ day: string; bytes: string }>(sql`
    WITH d AS (
      SELECT
        s.captured_at,
        ${series.delta} AS delta
      FROM ${fromWhere}
      WINDOW w AS (${series.window})
    )
    SELECT
      to_char(date_trunc('day', d.captured_at AT TIME ZONE 'UTC'), 'YYYY-MM-DD') AS day,
      coalesce(sum(d.delta), 0) AS bytes
    FROM d
    ${sinceFilter}
    GROUP BY day
    ORDER BY day
  `)

  const points = [...rows].map((r) => ({
    date: r.day,
    bytes: Number(r.bytes),
  }))
  return fillDailySeries(points, range, new Date())
}

/** Usage for a single credential, asserting it belongs to the caller's org. */
export const getCredentialUsage = createServerFn({ method: 'GET' })
  .validator((d: { credentialId: string; range: UsageRange }) => ({
    credentialId: parseInput(z.object({ credentialId: zId }), d).credentialId,
    range: normalizeRange(d.range),
  }))
  .handler(async ({ data }): Promise<UsagePoint[]> => {
    const { organizationId } = await requireOrgMember()
    const [owned] = await db
      .select({ id: proxyCredentials.id })
      .from(proxyCredentials)
      .where(
        and(
          eq(proxyCredentials.id, data.credentialId),
          eq(proxyCredentials.organizationId, organizationId),
        ),
      )
      .limit(1)
    if (!owned) throw new PublicError('Credential not found', 404)

    return dailyUsage(
      sql`usage_snapshots s WHERE s.credential_id = ${data.credentialId}`,
      data.range,
      credentialSeries,
    )
  })

/** Total consumed bytes over the range for one credential. */
export interface CredentialUsageTotal {
  credentialId: string
  bytes: number
}

/**
 * Per-credential consumed-bytes totals for every credential in the caller's
 * active org over the range, in one grouped query. Credentials with no snapshots
 * in the range are absent from the result.
 */
export const getOrgCredentialUsage = createServerFn({ method: 'GET' })
  .validator((d: { range: UsageRange }) => ({ range: normalizeRange(d.range) }))
  .handler(async ({ data }): Promise<CredentialUsageTotal[]> => {
    const { organizationId } = await requireOrgMember()
    const days = rangeDays(data.range)
    const sinceFilter = days
      ? sql`WHERE d.captured_at >= now() - (${sql.raw(String(days))} || ' days')::interval`
      : sql``

    const rows = await db.execute<{ credential_id: string; bytes: string }>(sql`
      WITH d AS (
        SELECT
          s.credential_id,
          s.captured_at,
          ${consumedDeltaSql} AS delta
        FROM usage_snapshots s WHERE s.organization_id = ${organizationId}
        WINDOW w AS (PARTITION BY s.credential_id ORDER BY s.captured_at)
      )
      SELECT d.credential_id, coalesce(sum(d.delta), 0) AS bytes
      FROM d
      ${sinceFilter}
      GROUP BY d.credential_id
    `)

    return [...rows].map((r) => ({
      credentialId: r.credential_id,
      bytes: Number(r.bytes),
    }))
  })

/**
 * Aggregate usage for the caller's active org, read from the org's own group
 * counter — the same counter the bill uses. Credential-level rows aren't summed
 * here, so a deleted credential's traffic still counts (it never left the group
 * counter) without needing a join through `proxy_credentials`.
 */
export const getOrgUsage = createServerFn({ method: 'GET' })
  .validator((d: { range: UsageRange }) => ({ range: normalizeRange(d.range) }))
  .handler(async ({ data }): Promise<UsagePoint[]> => {
    const { organizationId } = await requireOrgMember()
    return dailyUsage(
      sql`group_usage_snapshots s WHERE s.organization_id = ${organizationId}`,
      data.range,
      groupSeries,
    )
  })

/**
 * Consumed bytes in the window immediately BEFORE the current one, for a
 * period-over-period comparison on the usage strip. A `30d` range compares
 * against the 30 days before the last 30; `all` has no prior period, so this
 * returns `null` (the caller hides the trend).
 *
 * Read from the org's own group counter — the same series `getOrgUsage`
 * plots — with the same delta rule, so the strip's current total (summed from
 * that series client-side) and this previous total are directly comparable.
 * Only the scalar sum crosses the wire, not a second daily series.
 *
 * The delta is computed over ALL of the org's snapshots and only then filtered
 * to the prior window, so the window's first day still gets a correct delta
 * from the snapshot just before it (matching `dailyUsage`).
 */
export const getOrgUsagePreviousTotal = createServerFn({ method: 'GET' })
  .validator((d: { range: UsageRange }) => ({ range: normalizeRange(d.range) }))
  .handler(async ({ data }): Promise<{ bytes: number | null }> => {
    const { organizationId } = await requireOrgMember()
    const days = rangeDays(data.range)
    if (days == null) return { bytes: null }

    const rows = await db.execute<{ bytes: string }>(sql`
      WITH d AS (
        SELECT
          s.captured_at,
          ${groupConsumedDeltaSql} AS delta
        FROM group_usage_snapshots s WHERE s.organization_id = ${organizationId}
        WINDOW w AS (PARTITION BY s.organization_id ORDER BY s.captured_at)
      )
      SELECT coalesce(sum(d.delta), 0) AS bytes
      FROM d
      WHERE d.captured_at >= now() - (${sql.raw(String(days * 2))} || ' days')::interval
        AND d.captured_at <  now() - (${sql.raw(String(days))} || ' days')::interval
    `)

    return { bytes: Number(rows[0]?.bytes ?? 0) }
  })

/** Aggregate usage for one org (platform-admin view, any org). */
export const getOrgUsageAdmin = createServerFn({ method: 'GET' })
  .validator((d: { organizationId: string; range: UsageRange }) => ({
    organizationId: parseInput(z.object({ organizationId: zId }), d)
      .organizationId,
    range: normalizeRange(d.range),
  }))
  .handler(async ({ data }): Promise<UsagePoint[]> => {
    await requirePlatformAdmin()
    return dailyUsage(
      sql`group_usage_snapshots s WHERE s.organization_id = ${data.organizationId}`,
      data.range,
      groupSeries,
    )
  })

/**
 * Product-wide usage: every org's group counter summed per day (admin only).
 * Per-org partitioning keeps each org's deltas independent, so one org's
 * counter regression or first-ever row can't distort another's.
 */
export const getProductUsage = createServerFn({ method: 'GET' })
  .validator((d: { range: UsageRange }) => ({ range: normalizeRange(d.range) }))
  .handler(async ({ data }): Promise<UsagePoint[]> => {
    await requirePlatformAdmin()
    return dailyUsage(sql`group_usage_snapshots s`, data.range, groupSeries)
  })
